<template>
  <v-dialog
    :model-value="open"
    max-width="960px"
    scrollable
    persistent
    @update:model-value="onDialogUpdate"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Video To Agent</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" size="small" :disabled="processing" @click="closeDialog" />
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <p class="text-body-2 mb-4">
          Upload a short video showing the agent action or workflow you want to replicate. Optionally trim the clip
          to just the relevant section before processing.
        </p>

        <v-alert
          v-if="processing"
          type="warning"
          density="compact"
          variant="outlined"
          class="mb-4"
        >
          Do not refresh the page, navigate away, or close this dialog while a workflow video is in progress. The
          current video-processing state will be lost if the page reloads.
        </v-alert>

        <v-file-input
          :model-value="selectedFile"
          accept="video/mp4,video/quicktime,video/x-matroska,video/webm,video/x-msvideo,video/mp2t,video/mxf,video/x-flv,video/x-m4v,video/3gpp,video/3gpp2,.mp4,.mov,.mkv,.webm,.avi,.mxf,.flv,.m4v,.3gp,.3g2"
          label="Workflow Video"
          prepend-icon="mdi-video-outline"
          variant="outlined"
          density="comfortable"
          show-size
          hide-details
          @update:model-value="onFileSelected"
        />

        <v-alert
          v-if="errorMessage"
          type="error"
          density="compact"
          variant="tonal"
          class="mt-4"
        >
          {{ errorMessage }}
        </v-alert>

        <v-card v-if="videoPreviewUrl" variant="outlined" class="mt-4">
          <v-card-text>
            <video
              ref="previewVideo"
              :src="videoPreviewUrl"
              controls
              class="video-to-agent-preview"
              @loadedmetadata="onLoadedMetadata"
              @timeupdate="onVideoTimeUpdate"
            />

            <v-row class="mt-2" density="compact">
              <v-col cols="12">
                <div class="d-flex justify-space-between text-caption mb-1">
                  <span>Clip in/out</span>
                  <span>{{ formatTime(clipRange[0]) }} - {{ formatTime(clipRange[1]) }}</span>
                </div>
                <v-range-slider
                  :model-value="clipRange"
                  :min="0"
                  :max="durationSec"
                  :step="0.1"
                  color="primary"
                  track-color="secondary"
                  hide-details
                  @update:model-value="updateClipRange"
                />
              </v-col>
            </v-row>

            <div class="d-flex justify-end gap-2 mt-1">
              <v-btn variant="text" :disabled="processing" @click="jumpToClipBoundary('start')">
                Jump To In
              </v-btn>
              <v-btn variant="text" :disabled="processing" @click="jumpToClipBoundary('end')">
                Jump To Out
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <v-card v-if="processing" variant="outlined" class="mt-4">
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2">mdi-progress-clock</v-icon>
              <span>{{ processingLabel }}</span>
            </div>
            <v-progress-linear indeterminate color="primary" height="6" />
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn variant="text" :disabled="processing" @click="closeDialog">
          Close
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="processing || !canProcess"
          @click="emitProcess"
        >
          Process Video
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'VideoToAgentDialog',
  props: {
    open: {
      type: Boolean,
      required: true
    },
    processing: {
      type: Boolean,
      required: true
    },
    processingLabel: {
      type: String,
      required: true
    },
    errorMessage: {
      type: String,
      default: null
    }
  },
  emits: ['update:open', 'process'],
  data() {
    return {
      selectedFile: null as File | null,
      videoPreviewUrl: null as string | null,
      durationSec: 0,
      clipRange: [0, 0] as [number, number]
    }
  },
  computed: {
    canProcess(): boolean {
      return this.selectedFile != null && this.durationSec > 0 && this.clipRange[1] > this.clipRange[0]
    }
  },
  watch: {
    open(nextValue: boolean) {
      if (!nextValue && !this.processing) {
        this.resetLocalState()
      }
    }
  },
  beforeUnmount() {
    this.revokePreviewUrl()
  },
  methods: {
    onDialogUpdate(value: boolean): void {
      this.$emit('update:open', value)
    },
    closeDialog(): void {
      this.$emit('update:open', false)
    },
    onFileSelected(value: File | File[] | null): void {
      const file = Array.isArray(value) ? value[0] ?? null : value
      this.selectedFile = file
      this.revokePreviewUrl()
      this.durationSec = 0
      this.clipRange = [0, 0]

      if (!file) {
        return
      }

      this.videoPreviewUrl = URL.createObjectURL(file)
    },
    onLoadedMetadata(event: Event): void {
      const video = event.target as HTMLVideoElement | null
      const duration = video?.duration ?? 0
      if (!Number.isFinite(duration) || duration <= 0) {
        this.durationSec = 0
        this.clipRange = [0, 0]
        return
      }
      this.durationSec = duration
      this.clipRange = [0, duration]
    },
    onVideoTimeUpdate(event: Event): void {
      const video = event.target as HTMLVideoElement | null
      if (!video) {
        return
      }
      if (video.currentTime > this.clipRange[1]) {
        video.currentTime = this.clipRange[0]
      }
    },
    updateClipRange(value: [number, number] | number[]): void {
      if (!Array.isArray(value) || value.length < 2) {
        return
      }
      const start = Math.max(0, Math.min(value[0], value[1]))
      const end = Math.min(this.durationSec, Math.max(value[0], value[1]))
      this.clipRange = [start, end]
      const video = this.$refs.previewVideo as HTMLVideoElement | undefined
      if (video && video.currentTime < start) {
        video.currentTime = start
      }
    },
    jumpToClipBoundary(boundary: 'start' | 'end'): void {
      const target = boundary === 'start' ? this.clipRange[0] : this.clipRange[1]
      const video = this.$refs.previewVideo as HTMLVideoElement | undefined
      if (video) {
        video.currentTime = target
      }
    },
    emitProcess(): void {
      if (!this.selectedFile || !this.canProcess) {
        return
      }
      this.$emit('process', {
        file: this.selectedFile,
        clipStartSec: this.clipRange[0],
        clipEndSec: this.clipRange[1]
      })
    },
    formatTime(seconds: number): string {
      if (!Number.isFinite(seconds) || seconds <= 0) {
        return '0:00'
      }
      const totalSeconds = Math.floor(seconds)
      const minutes = Math.floor(totalSeconds / 60)
      const remainder = totalSeconds % 60
      return `${minutes}:${String(remainder).padStart(2, '0')}`
    },
    revokePreviewUrl(): void {
      if (this.videoPreviewUrl) {
        URL.revokeObjectURL(this.videoPreviewUrl)
        this.videoPreviewUrl = null
      }
    },
    resetLocalState(): void {
      this.selectedFile = null
      this.durationSec = 0
      this.clipRange = [0, 0]
      this.revokePreviewUrl()
    }
  }
})
</script>

<style scoped lang="scss">
.video-to-agent-preview {
  width: 100%;
  max-height: 420px;
  border-radius: 8px;
  background: rgb(var(--v-theme-surface-variant));
}
</style>
